import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { inject, ModuleWithProviders, NgModule } from '@angular/core';
import { ErrorPolicy, WatchQueryFetchPolicy, InMemoryCache } from '@apollo/client/core';
import { HttpHeadersInterceptor } from '@tgx/shared/data-access/interceptors';
import {
  AuthService,
  GtmDataLayerService,
  PlatformAuthService,
  PlatformHeadService,
  PlatformInfoService,
  PlatformSelectorOrganizationService,
  ProfilesService,
  AUTH0_DOMAIN,
  AUTH0_CLIENT_ID,
  URL_WEB_BFF,
  URL_CONTENT,
  URL_BILLING_CALCULATION,
  URL_GATEWAY,
  URL_NETWORK_SERVICE,
  URL_NOTIFICATIONS_SERVICE,
  URL_TICKETS_SERVICE,
  URL_MAPPING_SERVICE,
  URL_MAP_ROOMS_SERVICE,
  URL_DOCUSIGN,
  PRODUCTION_BUILD,
  APP_ENVIRONMENT,
} from '@tgx/shared/services';
import { ToastsService } from '@tgx/shared/toasts';
import { provideNamedApollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { environment } from './../../../../../environments/environment';
import { GqlContextName } from '@tgx/shared/data-access/graphql';

import { provideOptimus } from '@openng/optimus-ui/config';
import { definePreset } from '@openng/optimus-ui-themes';
import Material from '@openng/optimus-ui-themes/material';

const CustomTheme = definePreset(Material, {
  semantic: {
    primary: {
      // COLOR PRINCIPAL
      50: '#e3f2ff',
      100: '#b3d9ff',
      200: '#80bfff',
      300: '#4da6ff',
      400: '#1a8cff',
      500: '#0073e6', // color base
      600: '#005bb4',
      700: '#004282',
      800: '#002951',
      900: '#001021',
    },
  },
});

const providers = [
  {
    provide: AUTH0_DOMAIN,
    useValue: environment.auth0.domain,
  },
  {
    provide: AUTH0_CLIENT_ID,
    useValue: environment.auth0.clientId,
  },
  {
    provide: URL_WEB_BFF,
    useValue: environment.urlWebBff,
  },
  {
    provide: URL_CONTENT,
    useValue: environment.apis.content,
  },
  {
    provide: URL_BILLING_CALCULATION,
    useValue: environment.apis.billing_calculation,
  },
  {
    provide: URL_GATEWAY,
    useValue: environment.apis.gateway,
  },
  {
    provide: URL_NETWORK_SERVICE,
    useValue: environment.apis.network_service,
  },
  {
    provide: URL_NOTIFICATIONS_SERVICE,
    useValue: environment.apis.notifications_service,
  },
  {
    provide: URL_TICKETS_SERVICE,
    useValue: environment.apis.tickets_service,
  },
  {
    provide: URL_MAPPING_SERVICE,
    useValue: environment.apis.mapping,
  },
  {
    provide: URL_MAP_ROOMS_SERVICE,
    useValue: environment.apis.mapping_rooms,
  },
  {
    provide: URL_DOCUSIGN,
    useValue: environment.apis.docusign,
  },
  {
    provide: PRODUCTION_BUILD,
    useValue: environment.production,
  },
  {
    provide: APP_ENVIRONMENT,
    useValue: environment.env,
  },
  provideOptimus({
    theme: {
      preset: CustomTheme,
      options: {
        darkModeSelector: '.app-dark', // clase que activaría modo DARK si estuviera, sino se queda modo light.
      },
    },
  }),
  PlatformInfoService,
  PlatformAuthService,
  PlatformSelectorOrganizationService,
  PlatformHeadService,
  GtmDataLayerService,
  AuthService,
  {
    provide: HTTP_INTERCEPTORS,
    useClass: HttpHeadersInterceptor,
    multi: true,
  },
  provideHttpClient(withInterceptorsFromDi(), withFetch()),
  ProfilesService,
  ToastsService,
  //ApolloClients via new apollo-angular v12
  provideNamedApollo(() => {
    const queryPolicy: WatchQueryFetchPolicy = 'network-only';
    const errorPolicy: ErrorPolicy = 'all';

    const httpLink = inject(HttpLink);

    const client = (uri: string) => ({
      link: httpLink.create({
        uri: uri,
      }),
      cache: new InMemoryCache({
        typePolicies: {
          Query: {
            fields: {
              activation: {
                merge: true,
              },
              admin: {
                merge: true,
              },
              alertsX: {
                merge: true,
              },
              application: {
                merge: true,
              },
              businessRules: {
                merge: true,
              },
              hotelX: {
                merge: true,
              },
              infraestructure: {
                merge: true,
              },
              insights: {
                merge: true,
              },
              inventory: {
                merge: true,
              },
              logging: {
                merge: true,
              },
              package: {
                merge: true,
              },
              reconciliation: {
                merge: true,
              },
              searchCriteria: {
                merge: true,
              },
              social: {
                merge: true,
              },
              speed: {
                merge: true,
              },
              stats: {
                merge: true,
              },
              trafficOptimization: {
                merge: true,
              },
              transport: {
                merge: true,
              },
            },
          },
        },
      }),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: queryPolicy,
          addTypename: false,
        },
        query: {
          fetchPolicy: queryPolicy,
          addTypename: false,
        },
        mutate: {
          errorPolicy: errorPolicy,
        },
      },
      addTypename: false,
    });

    const clients = {
      default: client(environment.apis.gateway),
      [GqlContextName.GATEWAY]: client(environment.apis.gateway),
      [GqlContextName.BILLING]: client(environment.apis.billing_api),
      [GqlContextName.IAM]: client(environment.apis.iam_web),
      [GqlContextName.SOCIAL]: client(environment.apis.social),
      [GqlContextName.BOOKING_FLOW]: client(environment.apis.booking_flow),
    };

    return clients;
  }),
];

@NgModule({
  imports: [CommonModule],
  declarations: [],
  providers: providers,
  exports: [],
})
export class CoreModuleWeb {
  static forRoot(): ModuleWithProviders<CoreModuleWeb> {
    return {
      ngModule: CoreModuleWeb,
      providers: providers,
    };
  }

  constructor() {
    const parentModule = inject(CoreModuleWeb, { optional: true, skipSelf: true });

    if (parentModule) {
      throw new Error('CoreModulePrivate is already loaded. Import it in the AppModule only');
    }
  }
}
